/* @flow */

import classNames from 'classnames';
import * as React from 'react';
import ReactModal from 'react-modal';
import { Transition } from 'react-transition-group';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

import { modalId } from 'src/shared/app/base/constant/domConstants';
import useModalStatusContext from 'src/shared/app/base/modal/hook/useModalStatusContext';

import styles from './BaseModal.style';
import usePortalRegister from 'src/shared/app/base/portal/notification/hook/usePortalRegister';
import { notificationTransitionDuration } from 'src/shared/app/notification/constant/notificationConstants';

type Props = {
  children: React.Node,
  width?: string | number,
  shouldCloseOnOverlayClick?: boolean,
  onAfterOpen?: () => void,
  handleClose?: () => void,
  onAfterClose?: () => void,
  isOpen?: boolean,
  isAnimated?: boolean,
  state?: string,
  ariaLabelledBy?: string,
  ariaDescribedBy?: string,
  contentClassName?: string,
};

const useStyles = createUseThemeStyles(styles);

const ModalElt = ({
  children,
  width,
  handleClose,
  onAfterClose,
  onAfterOpen,
  shouldCloseOnOverlayClick = true,
  isOpen = true,
  isAnimated,
  state,
  ariaLabelledBy = undefined,
  ariaDescribedBy = undefined,
  contentClassName = undefined,
}: Props) => {
  // TODO - replace useModalStatusContext by usePortalRegister
  const { register, unregister } = useModalStatusContext();
  usePortalRegister(modalId);

  const selfId = React.useRef<symbol>(Symbol());

  React.useEffect(() => {
    if (isOpen) {
      register(selfId.current);
    } else {
      unregister(selfId.current);
    }
    return () => {
      unregister(selfId.current);
    };
  }, []);

  const handleAfterClose = () => {
    if (isOpen) return;

    unregister(selfId.current);
    onAfterClose && onAfterClose();
  };

  const classes = useStyles({ width });
  const getAnimationClasses = (state) => {
    if (!isAnimated) return '';
    switch (state) {
      case Transition.ENTERING:
        return classes.contentEntering;
      case Transition.EXITING:
      case Transition.EXITED:
        return classes.contentExiting;
    }
  };
  return (
    <ReactModal
      className={classNames(
        classes.content,
        contentClassName,
        getAnimationClasses(state),
      )}
      isOpen={isOpen}
      closeTimeoutMS={500}
      htmlOpenClassName={classes.html}
      bodyOpenClassName={classes.body}
      overlayClassName={{
        base: classNames(classes.overlay),
        afterOpen: classes['overlay-afterOpen'],
        beforeClose: classes['overlay-beforeClose'],
      }}
      aria={{
        labelledby: ariaLabelledBy,
        describedby: ariaDescribedBy,
      }}
      parentSelector={() => document.getElementById(modalId)}
      onAfterOpen={onAfterOpen}
      onAfterClose={handleAfterClose}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      onRequestClose={handleClose}
    >
      {children}
    </ReactModal>
  );
};

const Modal = React.memo(ModalElt);

const BaseModal = (props: Props): React.Node => {
  const { isOpen, isAnimated = false } = props;
  /* *Note: having isOpen undefined will result in not being
  able to animate the modal. Also make sure that isOpen value is changed
  from outside of the component. */
  if (!isAnimated || isOpen === undefined) return <Modal {...props} />;
  return (
    <Transition
      in={isOpen}
      timeout={notificationTransitionDuration}
      enter
      exit
      appear
    >
      {(state) => <Modal {...props} state={state} />}
    </Transition>
  );
};

export default React.memo<Props>(BaseModal);
